/**
 * Copyright 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { Else, If, Then } from "react-if";
import {
  Button,
  ExtendedTooltip,
  FlexContainer,
  ModalConfirm,
  SVGIcon,
  Text,
  theme,
} from "@nordcloud/gnui";
import { formatMoney } from "~/tools";

type Props = {
  editing: boolean;
  enableSave: boolean;
  shouldAllowRestoration: boolean;
  isBudgetGenerated: boolean;
  hasAnyChanged: boolean;
  currency: string;
  setEditing: (value: boolean) => void;
  restoreBudget: () => void;
  onCancel: () => void;
  previousBudget: number;
  yearlyBudget: number;
  onSave: () => void;
};

export function BudgetBreakdownActions({
  editing,
  setEditing,
  enableSave,
  isBudgetGenerated,
  hasAnyChanged,
  shouldAllowRestoration,
  restoreBudget,
  onCancel,
  previousBudget,
  currency,
  yearlyBudget,
  onSave,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);

  const caption = shouldAllowRestoration
    ? "Create your new Yearly Budget based on the Previous Year data"
    : null;

  return (
    <FlexContainer>
      <If condition={editing}>
        <Then>
          <FlexContainer columnGap={theme.spacing.spacing02}>
            <Button
              disabled={!enableSave}
              data-testid="budget-save"
              onClick={() => setIsOpen(true)}
            >
              Save Budget
            </Button>
            <ExtendedTooltip caption={caption}>
              <Button
                icon="costRefresh"
                disabled={!shouldAllowRestoration}
                data-testid="budget-restore"
                severity="low"
                onClick={restoreBudget}
              />
            </ExtendedTooltip>
            <Button icon="close" severity="low" onClick={onCancel} />
          </FlexContainer>
        </Then>
        <Else>
          <Button data-testid="budget-edit" onClick={() => setEditing(true)}>
            Edit Budget
          </Button>
        </Else>
      </If>
      <ModalConfirm
        isOpen={isOpen}
        actionLabel="Save"
        contentLabel="Save Budget"
        confirm={() => {
          onSave();
          setIsOpen(false);
        }}
        onClose={() => setIsOpen(false)}
      >
        <If condition={isBudgetGenerated && !hasAnyChanged}>
          <Then>
            <Text>Are you sure you want to save auto-generated budget?</Text>
          </Then>
          <Else>
            <ModalContent
              currency={currency}
              yearlyBudget={yearlyBudget}
              previousBudget={previousBudget}
            />
          </Else>
        </If>
      </ModalConfirm>
    </FlexContainer>
  );
}

type ModalContentProps = {
  currency: string;
  previousBudget: number;
  yearlyBudget: number;
};

function ModalContent({
  currency,
  previousBudget,
  yearlyBudget,
}: ModalContentProps) {
  return (
    <FlexContainer direction="column" alignItems="start">
      <Text>Are you sure you want to save the new budget?</Text>
      <FlexContainer columnGap={theme.spacing.spacing06}>
        <FlexContainer direction="column" alignItems="start">
          <Text size="sm" color={theme.color.text.text02} mb={0}>
            Yearly Budget before changes
          </Text>
          <Text weight="medium">
            {formatMoney(previousBudget, currency, false)}
          </Text>
        </FlexContainer>
        <FlexContainer>
          <SVGIcon name="arrowRight" />
        </FlexContainer>
        <FlexContainer direction="column" alignItems="start">
          <Text size="sm" color={theme.color.text.text02} mb={0}>
            Yearly Budget after changes
          </Text>
          <Text weight="medium">
            {formatMoney(yearlyBudget, currency, false)}
          </Text>
        </FlexContainer>
      </FlexContainer>
    </FlexContainer>
  );
}
